import { Component, ChangeDetectionStrategy, inject, OnInit, signal, ChangeDetectorRef } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { IrisDMSTag, IrisDMSTagI, dmsTagTypes, IrisDMSTagType } from '@iris/common/modules/dms/models/IrisDMSTag';
import { IrisDmsGridConfigurationService } from '@iris/modules/dms/grid-templates/dms-grid-configuration.service';
import { IrisDmsPermissionsSandbox } from '@iris/common/modules/dms/sandbox/dms-permissions.sandbox';
import { take, tap } from 'rxjs/operators';
import { IrisGlobalSandbox } from '@iris/common/redux/global.sandbox';
import {
  AgGridEvent,
  GridApi,
  GridOptions,
  GridReadyEvent,
  RowDataUpdatedEvent,
  RowSelectedEvent,
} from 'ag-grid-community';
import { IrisDmsTagsStore } from '../../../store/tags.signal-store';
import { AgGridModule } from 'ag-grid-angular';
import { AgGridPaginationModule } from '@iris/common/modules/grid/ag-grid/extensions/pagination/pagination.module';
import { AgGridDirectivesModule } from '@iris/common/modules/grid/ag-grid/directives/ag-grid.directives.module';
import { StrakitButtonComponent, StrakitSelectComponent, StrakitTextComponent } from '@ngx-strakit/cdk/controls';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { StrakitColorComponent } from '@ngx-strakit/cdk/controls/color';
import {
  SkModalBase,
  SkModalContentComponent,
  StrakitButtonPrefixDirective
} from '@ngx-strakit/cdk';

@Component({
  selector: 'iris-dms-select-tags-modal',
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    AgGridModule,
    AgGridPaginationModule,
    AgGridDirectivesModule,
    TranslatePipe,
    SkModalContentComponent,
    StrakitTextComponent,
    StrakitSelectComponent,
    StrakitColorComponent,
    StrakitButtonComponent,
    StrakitButtonPrefixDirective,
  ],
  templateUrl: './dms-select-tags-modal.component.html',
  styleUrls: ['./dms-select-tags-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    IrisDmsGridConfigurationService,
  ],
})
export class IrisDmsSelectTagsModalComponent extends SkModalBase<IrisDmsSelectTagsModalComponent, IrisDMSTagI[]> implements OnInit {
  readonly dialogRef: MatDialogRef<IrisDmsSelectTagsModalComponent>;
  private readonly formBuilder = inject(FormBuilder);
  private readonly globalSandbox = inject(IrisGlobalSandbox);
  private readonly dmsPermissionsSandbox = inject(IrisDmsPermissionsSandbox);
  private readonly dmsGridConfigurationService = inject(IrisDmsGridConfigurationService);
  private readonly changeDetector = inject(ChangeDetectorRef);
  private readonly translate = inject(TranslateService);
  private readonly tagsStore = inject(IrisDmsTagsStore);
  readonly data = inject<{
    selectedTags: IrisDMSTagI[];
  }>(MAT_DIALOG_DATA);

  selectedTags = [...this.data.selectedTags ?? []];

  readonly tagTypes = dmsTagTypes.map(tagType => ({
    name: tagType.name,
    label: this.translate.instant(tagType.label),
  }));

  readonly addTagForm: FormGroup = this.formBuilder.group({
    name: new FormControl<string>('', [Validators.required]),
    color: new FormControl<string>(''),
    type: new FormControl<IrisDMSTagType>(null),
  });

  readonly gridOptions: GridOptions<IrisDMSTagI> = {
    columnDefs: this.dmsGridConfigurationService.tagsGridDefaultColDef,
    rowSelection: {
      mode: 'multiRow',
      checkboxes: true,
      headerCheckbox: true,
      enableClickSelection: true,
      enableSelectionWithoutKeys: true,
    },
    floatingFiltersHeight: 40,
    defaultColDef: {
      resizable: false,
      filter: 'agTextColumnFilter',
      floatingFilter: true,
      suppressHeaderMenuButton: true,
      suppressFloatingFilterButton: true,
      suppressHeaderFilterButton: true,
    },
    onGridReady: (event: GridReadyEvent) => this.setGridApi(event),
    onRowDataUpdated: (event: RowDataUpdatedEvent) => this.updateSelection(event),
    onRowSelected: (event: RowSelectedEvent<IrisDMSTagI>) => this.onRowSelected(event),
  };
  readonly projectId = this.globalSandbox.currentProjectId;
  private readonly tagToSelect = signal<IrisDMSTagI>(null);
  private readonly gridApi = signal<GridApi>(null);
  readonly tags = this.tagsStore.getActivePublicAndProjectTags(this.projectId());

  get isAdmin(): boolean {
    return this.dmsPermissionsSandbox.isAdmin;
  }

  ngOnInit(): void {
    this.getTags();
    this.setTagType();
  }

  private getTags(): void {
    this.tagsStore.fetchPublicTags(false);
    this.tagsStore.fetchProjectTags({ projectId: this.projectId() });
  }

  private setTagType(): void {
    const tagTypeControl = this.addTagForm.get('type');

    if (!this.isAdmin) {
      tagTypeControl.setValue(IrisDMSTagType.Project);
    }

    const projectId = this.projectId();
    this.updateTypeDisableStatus();

    if (!projectId && tagTypeControl.value !== IrisDMSTagType.Public) {
      tagTypeControl.setValue(IrisDMSTagType.Public);
    }
  }

  private updateTypeDisableStatus(): void {
    if (!this.projectId() || !this.isAdmin) {
      this.addTagForm.get('type').disable();
    } else {
      this.addTagForm.get('type').enable();
    }
  }

  onRowSelected(event: RowSelectedEvent<IrisDMSTagI>): void {
    if (event.node.isSelected() === this.selectedTags.some(({ id }) => id === event.data.id)) {
      return;
    }

    if (event.node.isSelected()) {
      this.selectedTags = [...this.selectedTags, event.data];
    } else {
      this.selectedTags = this.selectedTags.filter(({ id }) => id !== event.data.id);
    }
  }

  addNewTag(): void {
    const { name, color, type } = this.addTagForm.getRawValue() as { name: string; color: string; type: IrisDMSTagType };
    const isProjectTag = type === IrisDMSTagType.Project;

    const tag = new IrisDMSTag({ name, value: name, color, projectId: isProjectTag ? this.projectId() : null });

    this.tagsStore.checkTagExistence$(tag.name, tag.projectId).pipe(
      take(1),
      tap(exists => {
        if (exists) {
          this.addTagForm.setErrors({ tagExists: true });
          this.changeDetector.markForCheck();
          return;
        }
        this.addTagForm.reset();
        this.addTagForm.setErrors(null);
        this.tagsStore.createTag(tag);
        this.tagToSelect.set(tag);
        this.gridApi()?.setFilterModel(null);
        this.setTagType();
      }),
    ).subscribe();
  }

  saveFileWithTags(): void {
    this.closeWithResult(this.selectedTags);
  }

  updateSelection(event: RowDataUpdatedEvent): void {
    this.setGridApi(event);
    event.api.forEachNode(node => {
      const tag = node.data as IrisDMSTagI;

      if (this.tagToSelect()) {
        const hasTagToSelectColor = !!this.tagToSelect().color;
        const hasTagColor = !!tag.color;
        const areColorsSame = hasTagColor && hasTagToSelectColor && tag.color === this.tagToSelect().color
          || !hasTagToSelectColor && !hasTagColor;
        const isTagMatch = tag.name === this.tagToSelect().name
          && tag.type === this.tagToSelect().type
          && areColorsSame;
        if (isTagMatch) {
          this.selectedTags.push(tag);
          this.tagToSelect.set(null);
          event.api.ensureIndexVisible(node.rowIndex, null);
        }
      }
      const selected = this.selectedTags.some(_tag => _tag.id === tag.id);
      node.setSelected(selected);
    });
  }

  private setGridApi(event: AgGridEvent): void {
    this.gridApi.set(event.api);
  }
}
